import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useAuth } from '../components/auth/AuthProvider';
import Header from '../components/layout/Header';
import MobileSidebar from '../components/layout/MobileSidebar';
import { getStudioUrl } from '../utils/domain';
import SEOHead from '../components/SEOHead';
import Logo from '../components/brand/Logo';
import SiteFooter from '../components/layout/SiteFooter';

const HERO = '/brand/hero-contact.png';
const SPARK = '/brand/spark-close.png';

export default function HomePage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [forceRender, setForceRender] = useState(false);

  useEffect(() => {
    if (loading) {
      const timeoutId = setTimeout(() => setForceRender(true), 3000);
      return () => clearTimeout(timeoutId);
    }
  }, [loading]);

  const enter = () => {
    if (user) {
      window.location.href = getStudioUrl();
      return;
    }
    navigate('/login');
  };

  if (loading && !forceRender) {
    return (
      <div className="min-h-screen bg-[var(--theme-base)] flex items-center justify-center">
        <Logo className="w-12 h-12" />
      </div>
    );
  }

  return (
    <main className="home-page">
      <SEOHead markdownUrl="https://clairify.fr/index.md" />
      <Header
        onMobileMenuToggle={() => setMobileMenuOpen(!mobileMenuOpen)}
        showMobileMenu={mobileMenuOpen}
      />
      <MobileSidebar
        isOpen={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
      />

      <section className="home-hero">
        <div className="home-hero-copy">
          <p className="home-kicker">Clarifier · Mémoire · France</p>
          <h1>
            Vos données,<br />
            enfin <em>claires</em>.
          </h1>
          <p className="home-lead">
            Clairify rend lisible ce que vos tableaux n'arrivent pas à dire.
            Une IA qui se souvient de votre entreprise — et nomme
            ce qui cloche.
          </p>
          <div className="home-hero-actions">
            <Button size="lg" onClick={enter}>
              {user ? 'Ouvrir le studio' : 'Commencer gratuitement'}
            </Button>
            <Link to="/pricing">Voir les tarifs</Link>
          </div>
          <p className="home-hero-note">Sans carte · 50 000 tokens · Hébergé à Paris</p>
        </div>
        <figure className="home-hero-still">
          <img
            src={HERO}
            alt="Main humaine et main robot, le contact qui clarifie"
          />
        </figure>
      </section>

      <section className="home-blind">
        <p className="home-kicker">L'angle mort</p>
        <h2>Trois outils. Le même brouillard.</h2>
        <div className="home-blind-grid">
          <article>
            <h3>ChatGPT</h3>
            <p>Oublie à chaque session. Vous recommencez. Encore.</p>
          </article>
          <article>
            <h3>Excel</h3>
            <p>Calcule tout. Ne comprend rien. Les contradictions restent.</p>
          </article>
          <article>
            <h3>Power BI</h3>
            <p>Raconte hier. Personne ne vous dit ce qui va casser.</p>
          </article>
        </div>
      </section>

      <section className="home-product">
        <figure className="home-product-still">
          <img src={SPARK} alt="Le point de clarté, entre deux doigts" />
        </figure>
        <div className="home-product-copy">
          <p className="home-kicker">Le studio</p>
          <h2>Elle ne traduit pas.<br /><em>Elle clarifie.</em></h2>
          <ul>
            <li>
              <strong>Memory Layer</strong>
              Une mémoire persistante de votre business. Plus vous l'utilisez,
              plus le brouillard se lève.
            </li>
            <li>
              <strong>Collisions</strong>
              Deux vérités qui s'annulent. Une promo qui booste le CA
              et détruit la marge. Elle les nomme.
            </li>
            <li>
              <strong>Multi-agents</strong>
              Détection, maturité, orchestration. Un orchestre —
              pas un modèle unique.
            </li>
          </ul>
        </div>
      </section>

      <section className="home-steps">
        <p className="home-kicker">Comment</p>
        <h2>Du fichier au clair.</h2>
        <ol>
          <li>
            <span>01</span>
            <div>
              <h3>Importez</h3>
              <p>Excel, CSV, PDF, vos sources. Tel quel.</p>
            </div>
          </li>
          <li>
            <span>02</span>
            <div>
              <h3>Elle mémorise</h3>
              <p>Patterns, historique, contexte. Rien ne se remet à zéro.</p>
            </div>
          </li>
          <li>
            <span>03</span>
            <div>
              <h3>Elle clarifie</h3>
              <p>Collisions, reco, actions. Vous décidez.</p>
            </div>
          </li>
        </ol>
      </section>

      <section className="home-sovereign">
        <p className="home-kicker">France</p>
        <h2>Vos données restent ici.</h2>
        <p>
          Scaleway, Paris. RGPD. Chiffrement. Hors Cloud Act.
          Clarifier n'est pas extraire.
        </p>
        <Link to="/privacy">Politique de confidentialité</Link>
      </section>

      <section className="home-close">
        <Logo className="w-12 h-12" />
        <h2>Passez au clair.</h2>
        <p>Gratuit. Sans carte. Studio ouvert.</p>
        <Button size="lg" onClick={enter}>
          {user ? 'Accéder au studio' : 'Créer un compte'}
        </Button>
      </section>

      <SiteFooter />
    </main>
  );
}
